/**
 * ファイルダウンロード用のユーティリティ関数
 */
/**
 * API ベース URL を取得
 */
export const getApiBaseUrl = () => {
    if (typeof import.meta !== "undefined" && import.meta.env?.VITE_API_URL) {
        return import.meta.env.VITE_API_URL;
    }
    return "http://localhost:3000";
};
/**
 * Cookie から値を取得
 */
export const getSecureCookie = (name) => {
    const nameEQ = name + "=";
    const ca = document.cookie.split(";");
    for (let i = 0; i < ca.length; i++) {
        let c = ca[i];
        while (c.charAt(0) === " ")
            c = c.substring(1, c.length);
        if (c.indexOf(nameEQ) === 0)
            return c.substring(nameEQ.length, c.length);
    }
    return null;
};
/**
 * ファイル名からContent-Typeを判定
 */
export const getContentTypeFromFileName = (fileName) => {
    const extension = fileName.split(".").pop()?.toLowerCase() || "";
    const contentTypeMap = {
        pdf: "application/pdf",
        jpg: "image/jpeg",
        jpeg: "image/jpeg",
        png: "image/png",
        gif: "image/gif",
        webp: "image/webp",
        txt: "text/plain",
        csv: "text/csv",
        xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        xls: "application/vnd.ms-excel",
        docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        doc: "application/msword",
    };
    return contentTypeMap[extension] || "application/octet-stream";
};
const MAX_JSON_RESPONSE_SIZE = 50 * 1024 * 1024; // 50MB に増やす
const ERROR_TEXT_SAMPLE_SIZE = 500;
export const processJsonResponse = (uint8Array, options = {}) => {
    const textDecoder = new TextDecoder();
    const text = textDecoder.decode(uint8Array.slice(0, ERROR_TEXT_SAMPLE_SIZE));
    const trimmedText = text.trim();
    if (trimmedText.startsWith("{") && !trimmedText.startsWith("{%")) {
        if (uint8Array.length > MAX_JSON_RESPONSE_SIZE) {
            return null;
        }
        let json;
        try {
            const jsonText = textDecoder.decode(uint8Array);
            json = JSON.parse(jsonText);
        }
        catch (error) {
            return null;
        }
        if (json.type === "Buffer" && Array.isArray(json.data)) {
            return new Uint8Array(json.data);
        }
        if (options.throwOnNonBufferJson) {
            const errorMsg = json.message || json.error || json.errorMessage || JSON.stringify(json);
            console.error("Server error response:", json);
            throw new Error(errorMsg);
        }
        return null;
    }
    return null;
};
/**
 * Blobを作成し、ダウンロードを実行
 */
export const createBlobAndDownload = (uint8Array, contentType, fileName) => {
    const blob = new Blob([uint8Array], { type: contentType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
};
/**
 * 署名付きURLからS3キーを抽出
 */
export const extractS3KeyFromUrl = (url) => {
    try {
        const urlObj = new URL(url);
        // Get pathname without leading slash and decode it
        return decodeURIComponent(urlObj.pathname.substring(1));
    }
    catch {
        console.error("Failed to parse URL:", url);
        return null;
    }
};
/**
 * ファイルをサーバー経由でダウンロード
 * @param s3Url - S3署名付きURL
 * @param fileName - ダウンロード時のファイル名
 */
export const downloadFileViaServer = async (s3Url, fileName = "download") => {
    try {
        // Base64データの場合は直接ダウンロード
        if (s3Url.startsWith("data:")) {
            const a = document.createElement("a");
            a.href = s3Url;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            return;
        }
        // S3キーを抽出
        const s3Key = extractS3KeyFromUrl(s3Url);
        if (!s3Key) {
            throw new Error("Failed to extract S3 key from URL");
        }
        // APIベースURLとトークンを取得
        const apiBaseUrl = getApiBaseUrl();
        const token = getSecureCookie("token");
        // サーバー経由でファイルを取得
        const response = await fetch(`${apiBaseUrl}/api/v1/download/file`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                ...(token && { Authorization: `Bearer ${token}` }),
            },
            body: JSON.stringify({ key: s3Key }),
        });
        if (!response.ok) {
            throw new Error(`Failed to download file: ${response.statusText}`);
        }
        const contentType = response.headers.get("content-type") ||
            getContentTypeFromFileName(fileName);
        const arrayBuffer = await response.arrayBuffer();
        const uint8Array = new Uint8Array(arrayBuffer);
        const dataToDownload = processJsonResponse(uint8Array) ?? uint8Array;
        createBlobAndDownload(dataToDownload, contentType, fileName);
    }
    catch (error) {
        console.error("Error downloading file:", error);
        throw error;
    }
};
/**
 * S3キーを直接指定してダウンロード
 * @param s3Key - S3オブジェクトキー (例: "0423994e.../logo/image.png")
 * @param fileName - ダウンロード時のファイル名
 */
export const downloadFileByKey = async (s3Key, fileName = "download") => {
    try {
        const apiBaseUrl = getApiBaseUrl();
        const token = getSecureCookie("token");
        const response = await fetch(`${apiBaseUrl}/api/v1/download/file`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                ...(token && { Authorization: `Bearer ${token}` }),
            },
            body: JSON.stringify({ key: s3Key }),
        });
        if (!response.ok) {
            throw new Error(`Failed to download file: ${response.statusText}`);
        }
        const contentType = response.headers.get("content-type") ||
            getContentTypeFromFileName(fileName);
        const arrayBuffer = await response.arrayBuffer();
        const uint8Array = new Uint8Array(arrayBuffer);
        const dataToDownload = processJsonResponse(uint8Array) ?? uint8Array;
        createBlobAndDownload(dataToDownload, contentType, fileName);
    }
    catch (error) {
        console.error("Error downloading file:", error);
        throw error;
    }
};
